import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useLocation, useNavigate, useInRouterContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AppTheme } from '@/theme/index';
import { useTheme } from '@/theme';
import { useMenuStore } from '@/store/menuStore';
import { useAuthStore } from '@/store';
import type { MenuItemType } from '@/menu/webMenu';
import { MenuItem } from './MenuItem';
import { Logo } from './components/Logo';
import { Account } from './components/Account';
import { Settings } from './components/Settings';
import { Logout } from './components/Logout';
import { Login } from './components/Login';
import { UserProfileModal } from '@/components/modals/UserProfileModal';
import { useMobileNavLayout, useSyncSidebarLayoutWithViewport } from '@/hooks/useMobileNavLayout';

const NavBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: calc(${({ theme }) => theme.zIndex.modal} - 1);
  background: rgba(0, 0, 0, 0.45);
`;

const SideBarWrap = styled.div<{ isOpen: boolean; $mobileNav: boolean }>`
  background-color: ${({ theme }) => theme.colors.surface.layout.container};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding-bottom: max(${({ theme }) => theme.spacing[4]}px, env(safe-area-inset-bottom, 0px));
  > * + * {
    margin-top: ${({ theme }) => theme.spacing[6]}px;
  }

  ${({ $mobileNav, isOpen, theme }) =>
    $mobileNav
      ? css`
          position: fixed;
          top: 0;
          left: 0;
          z-index: ${theme.zIndex.modal};
          height: 100dvh;
          max-height: 100dvh;
          width: min(300px, 86vw);
          padding-top: max(${theme.spacing[4]}px, env(safe-area-inset-top, 0px));
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          box-shadow: ${isOpen ? '4px 0 24px rgba(0, 0, 0, 0.12)' : 'none'};
          transform: translateX(${isOpen ? '0' : '-100%'});
          transition: transform 0.25s ease;
          cursor: default;
        `
      : css`
          height: 100%;
          width: ${isOpen ? '220px' : '68px'};
          transition: width 0.3s ease;
          padding-top: ${theme.spacing[4]}px;
          overflow: visible;
          cursor: ${isOpen ? 'default' : 'pointer'};
        `}
`;

const TopSection = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.divider};
  padding-bottom: ${({ theme }) => theme.spacing[4]}px;
`;

const MenuSection = styled.div`
  flex: 1;
`;

const SectionGroup = styled.div<{ hasDivider?: boolean }>`
  padding-bottom: ${({ theme, hasDivider }) => (hasDivider ? `${theme.spacing[4]}px` : '0')};
  margin-bottom: ${({ theme, hasDivider }) => (hasDivider ? `${theme.spacing[4]}px` : '0')};
  border-bottom: ${({ theme, hasDivider }) =>
    hasDivider ? `1px solid ${theme.colors.border.divider}` : 'none'};
`;

const SectionLabel = styled.div<{ isOpen?: boolean; theme: AppTheme }>`
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  padding-left: ${({ theme, isOpen }) =>
    isOpen ? `${theme.spacing[4]}px` : `${theme.spacing[1]}px`};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]}px;
  justify-content: ${({ isOpen }) => (isOpen ? 'flex-start' : 'center')};
  transition:
    font-size 0.3s ease-out,
    justify-content 0.3s;
`;

const SectionIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const SubMenu = styled.div<{ isOpen?: boolean }>`
  display: block;
`;

const LocalDeviceSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border.divider};
  background-color: ${({ theme }) => theme.colors.surface.group.default};
  padding-top: ${({ theme }) => theme.spacing[2]}px;
  padding-bottom: ${({ theme }) => theme.spacing[2]}px;
`;

const BottomGroup = styled.div`
  margin-top: auto;
`;

const BottomSection = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border.divider};
`;

// Inner implementation that assumes Router context exists
const SideBarInner: React.FC<{ menuData: MenuItemType[] }> = ({ menuData }) => {
  const { t } = useTranslation(['common', 'config']);
  const { theme } = useTheme();
  const { activeItem, isSidebarOpen, setActiveItem, setSidebarOpen } = useMenuStore();
  const { user, isAuthenticated } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const isMobileNav = useMobileNavLayout();

  useSyncSidebarLayoutWithViewport(setSidebarOpen);

  useEffect(() => {
    if (!isMobileNav || !isSidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isMobileNav, isSidebarOpen, setSidebarOpen]);

  useEffect(() => {
    if (!isMobileNav || !isSidebarOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileNav, isSidebarOpen]);

  // 메뉴 아이템을 재귀적으로 찾는 함수
  const findMenuItem = (items: MenuItemType[], targetId: string): MenuItemType | null => {
    for (const item of items) {
      if (item.id === targetId) {
        return item;
      }
      if (item.children) {
        const found = findMenuItem(item.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // 경로에서 메뉴 ID를 찾는 함수
  const findMenuIdByPath = (pathname: string): string => {
    const findByPath = (items: MenuItemType[]): string => {
      for (const item of items) {
        if (item.path === pathname) {
          return item.id;
        }
        if (item.children) {
          const found = findByPath(item.children);
          if (found) return found;
        }
      }
      return '';
    };
    return findByPath(menuData);
  };

  // URL과 메뉴 트리 동기화. 정책/인증 로드 전에는 menuData에 `/` 등이 없어 id를 못 찾으므로,
  // menuTree가 갱신될 때도 다시 매칭해야 TopBar(Overview 제목)가 복구된다.
  useEffect(() => {
    const menuId = findMenuIdByPath(location.pathname);
    if (menuId && menuId !== activeItem) {
      setActiveItem(menuId);
    }
  }, [location.pathname, activeItem, setActiveItem, menuData]);

  const handleItemClick = (itemId: string) => {
    const menuItem = findMenuItem(menuData, itemId);
    // 메뉴 아이템이 없거나 path가 없으면 무시
    if (!menuItem || !menuItem.path) {
      return;
    }
    setActiveItem(itemId);
    navigate(menuItem.path);
    if (isMobileNav) {
      setSidebarOpen(false);
    }
  };

  const renderMenuItem = (item: MenuItemType, level: number = 0, parentLabel?: string) => {
    const isActive = activeItem === item.id;
    const hasChildren = item.children && item.children.length > 0;

    // Determine icon to render
    let iconElement: string | React.ReactNode | undefined;

    iconElement = item.icon;

    return (
      <div key={item.id}>
        <MenuItem
          theme={theme}
          icon={iconElement}
          label={t(item.label)}
          isActive={isActive}
          hasChildren={false}
          isOpen={isSidebarOpen}
          level={level}
          onClick={() => {
            handleItemClick(item.id);
          }}
          parentLabel={parentLabel}
        />

        {hasChildren && (
          <SubMenu isOpen={isSidebarOpen}>
            {item.children!.map(child => renderMenuItem(child, level + 1, parentLabel))}
          </SubMenu>
        )}
      </div>
    );
  };

  const mainSections = menuData.filter(item => item.children && !item.section);
  const deviceSections = menuData.filter(item => item.children && item.section);

  const handleSidebarClick = () => {
    if (isMobileNav) return;
    if (!isSidebarOpen) {
      setSidebarOpen(true);
    }
  };

  return (
    <>
      {isMobileNav && isSidebarOpen && (
        <NavBackdrop aria-hidden onClick={() => setSidebarOpen(false)} />
      )}
      <SideBarWrap
        ref={sidebarRef}
        isOpen={isSidebarOpen}
        $mobileNav={isMobileNav}
        onClick={handleSidebarClick}
      >
        {/* A. Logo */}
        <Logo isOpen={isSidebarOpen} theme={theme} onToggle={() => setSidebarOpen(false)} />

        {isAuthenticated && (
          <TopSection>
            {user && (
              <Account
                userName={user.name || user.email}
                isOpen={isSidebarOpen}
                theme={theme}
                onClick={() => setShowUserProfileModal(true)}
              />
            )}
          </TopSection>
        )}

        {/* C. Menu Items  */}
        <MenuSection>
          {mainSections.map(section => (
            <SectionGroup key={section.id} hasDivider={section.hasDivider}>
              {section.children?.map(item => renderMenuItem(item, 0, section.section))}
            </SectionGroup>
          ))}
        </MenuSection>

        {/* Local Device + Bottom Section (grouped to stick together at bottom) */}
        <BottomGroup>
          {deviceSections.length > 0 && (
            <LocalDeviceSection>
              {deviceSections.map(section => (
                <div key={section.id}>
                  <SectionLabel isOpen={isSidebarOpen} theme={theme}>
                    {isSidebarOpen ? section.section : section.sectionShort}
                    {section.sectionIcon && <SectionIcon src={section.sectionIcon} alt="" />}
                  </SectionLabel>
                  {section.children?.map(item => renderMenuItem(item, 0, section.section))}
                </div>
              ))}
            </LocalDeviceSection>
          )}
          <BottomSection>
            <Settings isOpen={isSidebarOpen} theme={theme} />
            {isAuthenticated ? (
              <Logout isOpen={isSidebarOpen} theme={theme} />
            ) : (
              <Login isOpen={isSidebarOpen} theme={theme} />
            )}
          </BottomSection>
        </BottomGroup>
      </SideBarWrap>

      {/* E. User Profile Modal */}
      {user && (
        <UserProfileModal
          isVisible={showUserProfileModal}
          onClose={() => setShowUserProfileModal(false)}
          userId={user.id}
        />
      )}
    </>
  );
};

// Safe wrapper that avoids rendering when not inside a Router
export const SideBar: React.FC<{ menuData: MenuItemType[] }> = ({ menuData }) => {
  const inRouter = useInRouterContext();
  if (!inRouter) return null; // or render a placeholder
  return <SideBarInner menuData={menuData} />;
};
